import { useState } from "react";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { trpc } from "@/lib/trpc";
import { formatDateTime } from "@/lib/utils";
import { Copy, Eye, EyeOff, FileText, HandCoins } from "lucide-react";

// Espace freelance pro (05/10/2026) : demandes de versement de la cagnotte.
// L'admin télécharge le relevé (autofacture), fait le virement puis marque la
// demande payée avec la référence ; les écritures passent PAYÉES (audité).
// Endpoints : admin.commissions.payouts / markPayoutPaid / cancelPayout / payoutStatementPdf.

const eur = (c: number | null | undefined) => (c == null ? "-" : (c / 100).toLocaleString("fr-FR", { style: "currency", currency: "EUR" }));
const STATUS: Record<string, { label: string; variant: "default" | "secondary" | "outline" | "warning" }> = {
  REQUESTED: { label: "À virer", variant: "warning" },
  PAID: { label: "Payé", variant: "default" },
  CANCELLED: { label: "Annulé", variant: "outline" },
};

/** Projection locale (le type inféré du routeur est trop profond pour tsc). */
interface PayoutRow {
  id: string;
  number: string;
  status: string;
  amountCents: number;
  vatCents: number;
  totalCents: number;
  selfBilling: boolean;
  entries: number;
  requestedAt: string | Date;
  paidAt: string | Date | null;
  paymentReference: string | null;
  cancelReason: string | null;
  user: { id: string; email: string; name: string; staffRole: string | null };
}

function downloadPdf(fileName: string, base64: string) {
  const bytes = Uint8Array.from(atob(base64), (c) => c.charCodeAt(0));
  const url = URL.createObjectURL(new Blob([bytes], { type: "application/pdf" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  a.click();
  URL.revokeObjectURL(url);
}

export function FreelancePayoutsPanel() {
  const utils = trpc.useUtils();
  const [status, setStatus] = useState<"REQUESTED" | "PAID" | "CANCELLED" | "">("REQUESTED");
  const q = trpc.admin.commissions.payouts.useQuery(status ? { status } : {});
  const [refs, setRefs] = useState<Record<string, string>>({});
  const [cancelId, setCancelId] = useState<string | null>(null);
  const [reason, setReason] = useState("");
  const refresh = () => Promise.all([utils.admin.commissions.payouts.invalidate(), utils.admin.commissions.freelances.invalidate()]);
  const markPaid = trpc.admin.commissions.markPayoutPaid.useMutation({
    onSuccess: (r) => { toast.success(`Versement marqué payé (${r.paid} écriture(s)).`); void refresh(); },
    onError: (e) => toast.error(e.message),
  });
  const cancel = trpc.admin.commissions.cancelPayout.useMutation({
    onSuccess: () => { toast.success("Demande annulée, le freelance est prévenu."); setCancelId(null); setReason(""); void refresh(); },
    onError: (e) => toast.error(e.message),
  });
  const pdf = utils.admin.commissions.payoutStatementPdf;
  const rows: PayoutRow[] = (q.data ?? []) as unknown as PayoutRow[];
  // H2 (revue #359) : IBAN révélé à la demande (mutation auditée), masqué par
  // défaut, jamais conservé au-delà de l'état local de ce panneau.
  const [revealed, setRevealed] = useState<Record<string, { iban: string; holder: string | null; bic: string | null; visible: boolean }>>({});
  const reveal = trpc.admin.commissions.revealIban.useMutation({ onError: (e) => toast.error(e.message) });
  const fmtIban = (iban: string) => iban.replace(/(.{4})/g, "$1 ").trim();

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <CardTitle className="text-base flex items-center gap-2"><HandCoins className="h-4 w-4" /> Demandes de versement</CardTitle>
          <select className="h-8 rounded-md border border-input bg-background px-2 text-sm" value={status} onChange={(e) => setStatus(e.target.value as any)}>
            <option value="REQUESTED">À virer</option>
            <option value="PAID">Payées</option>
            <option value="CANCELLED">Annulées</option>
            <option value="">Toutes</option>
          </select>
        </div>
      </CardHeader>
      <CardContent className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>N°</TableHead>
              <TableHead>Freelance</TableHead>
              <TableHead>Demandé le</TableHead>
              <TableHead className="text-right">HT</TableHead>
              <TableHead className="text-right">TVA</TableHead>
              <TableHead className="text-right">À virer</TableHead>
              <TableHead>Statut</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((p) => (
              <TableRow key={p.id}>
                <TableCell className="font-mono text-xs">{p.number}<div className="text-[10px] text-muted-foreground">{p.entries} écriture(s){p.selfBilling ? " · autofacture" : ""}</div></TableCell>
                <TableCell><div className="text-sm">{p.user.name}</div><div className="text-xs text-muted-foreground">{p.user.email}</div></TableCell>
                <TableCell className="text-sm">{formatDateTime(p.requestedAt)}</TableCell>
                <TableCell className="text-right">{eur(p.amountCents)}</TableCell>
                <TableCell className="text-right">{eur(p.vatCents)}</TableCell>
                <TableCell className="text-right font-medium">{eur(p.totalCents)}</TableCell>
                <TableCell>
                  <Badge variant={STATUS[p.status]?.variant ?? "outline"}>{STATUS[p.status]?.label ?? p.status}</Badge>
                  {p.paidAt && <div className="text-xs text-muted-foreground">{formatDateTime(p.paidAt)} · {p.paymentReference}</div>}
                  {p.cancelReason && <div className="text-xs text-muted-foreground">{p.cancelReason}</div>}
                </TableCell>
                <TableCell>
                  <div className="flex flex-col gap-1 min-w-[220px]">
                    <Button size="sm" variant="outline" onClick={async () => {
                      try { const r = await pdf.fetch({ payoutId: p.id }); downloadPdf(r.fileName, r.base64); } catch (e: any) { toast.error(e?.message ?? "PDF indisponible"); }
                    }}><FileText className="h-3.5 w-3.5 mr-1" /> Relevé PDF</Button>
                    {p.status === "REQUESTED" && (
                      <>
                        <div className="rounded border bg-muted/40 p-1.5 text-xs space-y-1">
                          {revealed[p.id] ? (
                            <div className="flex items-center gap-1 flex-wrap">
                              <span className="font-mono">{revealed[p.id].visible ? fmtIban(revealed[p.id].iban) : `•••• ${revealed[p.id].iban.slice(-4)}`}</span>
                              <Button size="sm" variant="ghost" className="h-6 px-1" aria-label={revealed[p.id].visible ? "Masquer l'IBAN" : "Afficher l'IBAN"} onClick={() => setRevealed({ ...revealed, [p.id]: { ...revealed[p.id], visible: !revealed[p.id].visible } })}>
                                {revealed[p.id].visible ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                              </Button>
                              <Button size="sm" variant="ghost" className="h-6 px-1" aria-label="Copier l'IBAN" onClick={() => { void navigator.clipboard?.writeText(revealed[p.id].iban); toast.success("IBAN copié"); }}>
                                <Copy className="h-3.5 w-3.5" />
                              </Button>
                              {revealed[p.id].holder && <span className="text-muted-foreground">{revealed[p.id].holder}{revealed[p.id].bic ? ` · ${revealed[p.id].bic}` : ""}</span>}
                            </div>
                          ) : (
                            <Button size="sm" variant="outline" className="h-6" disabled={reveal.isLoading} onClick={async () => {
                              try {
                                const r = await reveal.mutateAsync({ userId: p.user.id, payoutId: p.id });
                                setRevealed({ ...revealed, [p.id]: { ...r, visible: false } });
                              } catch { /* toast via onError */ }
                            }}>Voir le RIB (audité)</Button>
                          )}
                        </div>
                        <div className="flex gap-1">
                          <Input className="h-8 text-xs" placeholder="Référence du virement" value={refs[p.id] ?? ""} onChange={(e) => setRefs({ ...refs, [p.id]: e.target.value })} />
                          <Button size="sm" disabled={(refs[p.id] ?? "").trim().length < 2 || markPaid.isLoading} onClick={() => markPaid.mutate({ payoutId: p.id, reference: refs[p.id].trim() })}>Payé</Button>
                        </div>
                        {cancelId === p.id ? (
                          <div className="flex gap-1">
                            <Input className="h-8 text-xs" placeholder="Motif (obligatoire)" value={reason} onChange={(e) => setReason(e.target.value)} />
                            <Button size="sm" variant="destructive" disabled={reason.trim().length < 3 || cancel.isLoading} onClick={() => cancel.mutate({ payoutId: p.id, reason: reason.trim() })}>Confirmer</Button>
                            <Button size="sm" variant="ghost" onClick={() => setCancelId(null)}>Non</Button>
                          </div>
                        ) : (
                          <Button size="sm" variant="ghost" className="text-destructive" onClick={() => setCancelId(p.id)}>Annuler la demande</Button>
                        )}
                      </>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {q.data && rows.length === 0 && (
              <TableRow><TableCell colSpan={8} className="text-sm text-muted-foreground">Aucune demande.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}
